import api from './api';

export const communicationService = {
  // Get communication threads (conversations grouped by client)
  getThreads: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
      ...(params.unread_only && { unread_only: params.unread_only }),
      ...(params.case_id && { case_id: params.case_id }),
      ...(params.search && { search: params.search }),
    });
    
    const response = await api.get(`/communications/threads?${queryParams}`);
    return response.data;
  },

  // Get all communications with filtering
  getCommunications: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
      ...(params.client_id && { client_id: params.client_id }),
      ...(params.case_id && { case_id: params.case_id }),
      ...(params.type && { type: params.type }),
      ...(params.unread_only && { unread_only: params.unread_only }),
    });
    
    const response = await api.get(`/communications?${queryParams}`);
    return response.data;
  },

  // Send SMS message
  sendSMS: async (messageData) => {
    const response = await api.post('/communications/sms', {
      client_id: messageData.client_id,
      case_id: messageData.case_id,
      content: messageData.content,
      to_phones: messageData.to_phones,
      to_phone: messageData.to_phone,
    });
    return response.data;
  },

  // Check for group SMS conflicts before sending
  // Returns conflict info if any phone numbers are already in group conversations
  checkGroupSmsConflicts: async (phones) => {
    if (!phones || phones.length < 2) {
      return { has_conflict: false };
    }
    try {
      const response = await api.post('/communications/sms/check-group-conflicts', phones);
      return response.data;
    } catch (error) {
      console.error('Error checking group SMS conflicts:', error);
      // If the check fails, don't block sending - let the server handle it
      return { has_conflict: false };
    }
  },

  // Send email message
  sendEmail: async (messageData) => {
    const response = await api.post('/communications/email', {
      client_id: messageData.client_id,
      case_id: messageData.case_id,
      subject: messageData.subject,
      content: messageData.content,
      to_email: messageData.to_email,
      cc_addresses: messageData.cc_addresses,
      bcc_addresses: messageData.bcc_addresses,
    });
    return response.data;
  },

  // Mark communication as read
  markAsRead: async (communicationId) => {
    const response = await api.put(`/communications/${communicationId}/read`);
    return response.data;
  },

  // Mark all unread messages across all clients as read
  markAllAsRead: async () => {
    const response = await api.put('/communications/mark-all-read');
    return response.data;
  },

  // Mark all messages from a client as read
  markClientMessagesAsRead: async (clientId) => {
    const communications = await communicationService.getCommunications({
      client_id: clientId,
      unread_only: true,
    });
    
    const promises = communications.communications.map(comm => 
      communicationService.markAsRead(comm.id)
    );
    
    return Promise.all(promises);
  },

  // Get communication statistics
  getStats: async (days = 30) => {
    const response = await api.get(`/communications/stats?days=${days}`);
    return response.data;
  },

  // Normalize phone number to E.164 format for Twilio
  // Accepts various formats and converts to +1XXXXXXXXXX for US numbers
  normalizePhoneNumber: (phone) => {
    if (!phone) return null;

    // Trim whitespace
    let normalized = phone.trim();

    // Handle international 00 prefix (European style)
    // Convert 00 to + (e.g., "00 1 435 535 1661" -> "+ 1 435 535 1661")
    if (normalized.startsWith('00')) {
      normalized = '+' + normalized.substring(2);
    }

    // Remove all non-digit characters (spaces, dashes, parentheses, etc.)
    const digits = normalized.replace(/\D/g, '');

    // Handle different US number formats
    if (digits.length === 10) {
      // 10 digits: assume US number, add country code
      // Example: 4355351661 -> +14355351661
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // 11 digits starting with 1: US number with country code
      // Example: 14355351661 -> +14355351661
      return `+${digits}`;
    } else if (digits.length >= 7 && digits.length <= 15) {
      // International number: add + if not present
      // Keep as-is but ensure it has + prefix
      return `+${digits}`;
    }

    // Invalid length
    return null;
  },

  // Format phone number for display
  formatPhoneNumber: (phone) => {
    if (!phone) return '';

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      // US format: (123) 456-7890
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // US format with country code: +1 (123) 456-7890
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // International or unknown format
    return phone;
  },

  // Check if phone number contains an extension
  hasExtension: (phone) => {
    if (!phone) return false;

    const extensionPatterns = [
      /x\s*\d+/i,             // x123, x 123, 1661x123
      /\bext\.?\s*\d+/i,      // ext123, ext. 123, ext 123
      /\bextension\s*\d+/i,   // extension 123
      /#\s*\d+/,              // #123, # 123
    ];

    return extensionPatterns.some(pattern => pattern.test(phone));
  },

  // Validate phone number (checks if it can be normalized)
  isValidPhoneNumber: (phone) => {
    if (!phone) return false;

    // Check for extensions - not supported
    if (communicationService.hasExtension(phone)) {
      return false;
    }

    // Handle international 00 prefix before extracting digits
    let normalized = phone.trim();
    if (normalized.startsWith('00')) {
      normalized = '+' + normalized.substring(2);
    }

    const digits = normalized.replace(/\D/g, '');

    // US numbers: 10 digits or 11 digits starting with 1
    // International: at least 7 digits, at most 15
    if (digits.length === 10) {
      // Check for valid US area code (cannot start with 0 or 1)
      const areaCode = digits.substring(0, 3);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
        return false;
      }
      return true;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // Check for valid US area code (cannot start with 0 or 1)
      const areaCode = digits.substring(1, 4);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
        return false;
      }
      return true;
    }

    // International: at least 7 digits, at most 15
    return digits.length >= 7 && digits.length <= 15;
  },

  // Get recent communications for dashboard widget
  getRecentCommunications: async (limit = 5) => {
    const response = await communicationService.getCommunications({
      size: limit,
      page: 1,
    });
    return response.communications;
  },

  // Get unread count for notifications
  getUnreadCount: async () => {
    const response = await communicationService.getStats(1);
    return response.unread;
  },

  // Real-time polling for new messages (simple implementation)
  pollForNewMessages: async (callback, interval = 30000) => {
    let lastCheck = new Date();

    const poll = async () => {
      try {
        const communications = await communicationService.getCommunications({
          size: 10,
          unread_only: true,
        });

        // Filter for messages received after last check
        const newMessages = communications.communications.filter(
          comm => new Date(comm.created_at) > lastCheck
        );

        if (newMessages.length > 0) {
          callback(newMessages);
        }

        lastCheck = new Date();
      } catch (error) {
        console.error('Error polling for new messages:', error);
      }
    };

    // Initial poll
    poll();

    // Set up interval
    const intervalId = setInterval(poll, interval);

    // Return cleanup function
    return () => clearInterval(intervalId);
  },

  // ========== CONVERSATION API METHODS ==========

  // Get conversations (multi-participant threads)
  getConversations: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
      ...(params.unread_only && { unread_only: params.unread_only }),
      ...(params.case_id && { case_id: params.case_id }),
      ...(params.type && { type: params.type }),
      ...(params.search && { search: params.search }),
    });

    const response = await api.get(`/conversations?${queryParams}`);
    return response.data;
  },

  // Get a specific conversation with all messages
  getConversationThread: async (conversationId) => {
    const response = await api.get(`/conversations/${conversationId}`);
    return response.data;
  },

  // Mark all messages in a conversation as read
  markConversationAsRead: async (conversationId) => {
    const response = await api.post(`/conversations/${conversationId}/read`);
    return response.data;
  },

  // Create a new conversation manually
  createConversation: async (conversationData) => {
    const response = await api.post('/conversations', conversationData);
    return response.data;
  },

  // ========== MUTE API METHODS ==========

  // Get muted conversations with full details
  getMutedConversations: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
    });
    const response = await api.get(`/conversations/muted/full?${queryParams}`);
    return response.data;
  },

  // Get list of muted conversation IDs (for quick checking)
  getMutedConversationIds: async () => {
    const response = await api.get('/conversations/muted');
    return response.data;
  },

  // Mute a conversation
  muteConversation: async (conversationId, duration = 'forever') => {
    const queryParams = duration ? `?duration=${duration}` : '';
    const response = await api.post(`/conversations/${conversationId}/mute${queryParams}`);
    return response.data;
  },

  // Unmute a conversation
  unmuteConversation: async (conversationId) => {
    const response = await api.delete(`/conversations/${conversationId}/mute`);
    return response.data;
  }
};

export default communicationService;